/**
 * companyDeletion — apagar EMPRESA (e "limpar todos os dados") sem meio-estado.
 *
 * ─── O BUG QUE ORIGINOU ESTE ARQUIVO (D15, achado em aparelho) ──────────────
 *
 * Duas decisões corretas isoladamente se contradiziam no Postgres:
 *
 *   finance_entries.company_id   NOT NULL   FK → companies ON DELETE SET NULL
 *   quotes.company_id            NOT NULL   FK → companies ON DELETE SET NULL
 *
 * Apagar a empresa fazia a FK tentar escrever NULL exatamente onde NULL era
 * proibido → **23502**, e o DELETE inteiro era recusado. Uma empresa com UM
 * lançamento financeiro (ou um orçamento) não podia ser excluída por caminho
 * nenhum.
 *
 * E o `DeleteDataPage` só descobria isso no QUARTO passo de cinco: orçamentos,
 * clientes e catálogo já tinham sido destruídos. O toast dizia "Erro ao limpar
 * dados. Tente novamente." — e tentar de novo nunca resolveria. Medido:
 * `quotes 1→0`, `clients 1→0`, `products 1→0`, `companies 1→1`.
 *
 * ─── A CORREÇÃO ─────────────────────────────────────────────────────────────
 *
 * As FKs viraram ON DELETE CASCADE (migração `20260804153436`), o que é o que
 * as outras ~30 tabelas com `company_id` já faziam e o que o NOT NULL sempre
 * quis dizer. Mas o CASCADE **sozinho não basta**: ao derrubar os orçamentos,
 * `finance_entries.quote_id` (SET NULL) esbarra no CHECK
 * `billable_requires_quote` → **23514**. A ordem entre ações de cascata não é
 * garantida pelo Postgres — no ensaio, um lançamento cobrável DA MESMA empresa
 * passou (morreu junto, por sorte de ordenação) e um de OUTRA empresa
 * apontando para o mesmo orçamento foi recusado. Sorte não é desenho.
 *
 * Por isso tudo passa por RPC: `delete_companies_cascade` solta a marcação
 * cobrável ANTES e apaga na MESMA transação — ou as duas coisas acontecem, ou
 * nenhuma. É a mesma porta que o #109 abriu para o orçamento, uma casa acima.
 *
 * `clear_user_data` faz a limpeza inteira numa transação só, o que elimina de
 * vez o meio-estado dos cinco deletes em sequência.
 */
import { supabase } from '@/integrations/supabase/client';

export interface CompanyDeletionCounts {
  /** Empresas efetivamente apagadas (a RPC ignora as que o usuário não é dono). */
  deleted: number;
  /** Despesas cobráveis que deixaram de ser cobráveis antes da queda. */
  released: number;
}

export interface ClearDataCounts extends CompanyDeletionCounts {
  /** Orçamentos que sobreviveram à queda das empresas (feitos em empresa de terceiro). */
  quotes: number;
}

function realIds(ids: readonly string[]): string[] {
  return Array.from(
    new Set(
      (ids ?? [])
        .map((id) => String(id ?? ''))
        // Um `offline_` nunca chegou ao Postgres — mandá-lo produz
        // `invalid input syntax for type uuid` e trava a fila inteira (#123d).
        .filter((id) => id.length > 0 && !id.startsWith('offline_')),
    ),
  );
}

/**
 * Apaga empresas ATOMICAMENTE, soltando antes as despesas cobráveis.
 *
 * Lança em erro, e é importante que lance: quem chama só pode mexer no estado
 * local (ou anunciar sucesso) depois que isto voltar sem erro.
 */
export async function deleteCompaniesCascade(
  companyIds: readonly string[],
): Promise<CompanyDeletionCounts> {
  const ids = realIds(companyIds);
  if (ids.length === 0) return { deleted: 0, released: 0 };

  const { data, error } = await supabase.rpc('delete_companies_cascade', {
    p_company_ids: ids,
  });
  if (error) throw error;

  const payload = (data ?? {}) as { deleted?: number; released?: number };
  return {
    deleted: Number(payload.deleted ?? 0),
    released: Number(payload.released ?? 0),
  };
}

/**
 * "Limpar todos os dados" — uma transação, no servidor.
 *
 * Não existe versão em passos no cliente de propósito: era justamente a
 * sequência de deletes que produzia o meio-estado irreversível.
 */
export async function clearUserData(): Promise<ClearDataCounts> {
  const { data, error } = await supabase.rpc('clear_user_data');
  if (error) throw error;

  const payload = (data ?? {}) as { companies?: number; released?: number; quotes?: number };
  return {
    deleted: Number(payload.companies ?? 0),
    released: Number(payload.released ?? 0),
    quotes: Number(payload.quotes ?? 0),
  };
}
